import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  Heading3,
  List as ListIcon,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Library as LibraryIcon,
  Palette as PaletteIcon,
  Highlighter as HighlighterIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const FONT_COLORS = [
  "#0a1128", "#475569", "#059669", "#0284c7",
  "#d946ef", "#f97316", "#eab308", "#ef4444",
];
const HIGHLIGHT_COLORS = [
  "#fef3c7", "#fee2e2", "#dcfce7", "#dbeafe",
  "#fce7f3", "#e0e7ff", "#fde68a", "#fed7aa",
];

/** Small inline color-swatch popover used inside the BubbleMenu. */
function ColorPicker({ icon, title, testid, colors, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        className={`h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 ${
          open ? "bg-slate-900 text-white hover:bg-slate-800" : ""
        }`}
        title={title}
        data-testid={testid}
      >
        {icon}
      </button>
      {open && (
        <div
          className="absolute z-50 top-full mt-1 left-0 bg-white border border-slate-200 rounded-lg shadow-xl p-2 w-44"
          data-testid={`${testid}-popover`}
        >
          <div className="grid grid-cols-4 gap-1.5">
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                }}
                className="h-7 w-7 rounded-md border border-slate-200 hover:scale-110 transition-transform"
                style={{ background: c }}
                title={c}
                data-testid={`${testid}-swatch-${c.replace("#", "")}`}
              />
            ))}
          </div>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onClear();
              setOpen(false);
            }}
            className="mt-2 w-full text-xs text-slate-500 hover:text-slate-800 py-1 border-t border-slate-100"
            data-testid={`${testid}-clear`}
          >
            Clear
          </button>
        </div>
      )}
    </span>
  );
}

/**
 * RichTextEditor — TipTap-powered rich text editor for placed text fields.
 *
 * Stores HTML in the field's value. The signer page renders the HTML
 * (sanitized) and the backend pdf_service converts the same HTML into reportlab
 * paragraphs for the signed PDF.
 *
 * Toolbar appears as a BubbleMenu (floats above selection) so the editor stays
 * compact and lives inside the field marker.
 *
 * Props:
 *   value: string (HTML)
 *   onChange: (html) => void
 *   onHeight: (px) => void  optional — called whenever the editor's measured
 *     scrollHeight changes so the parent field-marker can autogrow.
 *   color: string  — text colour
 *   testId: string
 *   readOnly?: boolean
 */
export default function RichTextEditor({
  value,
  onChange,
  onHeight,
  color = "#0a1128",
  testId,
  readOnly = false,
  enableSnippets = true,
}) {
  const [snippets, setSnippets] = useState([]);
  const [snippetsOpen, setSnippetsOpen] = useState(false);
  const [snippetsLoaded, setSnippetsLoaded] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: true }),
    ],
    content: value || "",
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html);
      if (onHeight) {
        // measure scrollHeight of the rendered content
        const el = ed.view?.dom;
        if (el) onHeight(el.scrollHeight);
      }
    },
    editorProps: {
      attributes: {
        class: "rt-editor",
        "data-testid": testId,
      },
      handleDOMEvents: {
        // prevent the parent field-marker's drag behavior while editing
        mousedown: (_, event) => {
          event.stopPropagation();
          return false;
        },
        dragstart: (_, event) => {
          event.preventDefault();
          return true;
        },
      },
    },
  });

  // Push external value changes into the editor (e.g., reload from server)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== (value || "")) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  // Initial scrollHeight on mount once content is parsed
  useEffect(() => {
    if (!editor || !onHeight) return;
    const t = setTimeout(() => {
      const el = editor.view?.dom;
      if (el) onHeight(el.scrollHeight);
    }, 30);
    return () => clearTimeout(t);
  }, [editor, onHeight]);

  if (!editor) {
    return <div className="text-xs text-slate-400 p-2">Loading editor…</div>;
  }

  const Btn = ({ on, active, title, children, testid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={on}
      className={`h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 ${
        active ? "bg-slate-900 text-white hover:bg-slate-800" : ""
      }`}
      title={title}
      data-testid={testid}
    >
      {children}
    </button>
  );

  return (
    <div className="rt-wrap w-full h-full" style={{ color }}>
      {!readOnly && (
        <BubbleMenu
          editor={editor}
          shouldShow={({ editor: ed }) => ed.isEditable && ed.isFocused}
          className="rt-toolbar flex flex-wrap items-center gap-0.5 bg-white border border-slate-200 rounded-lg shadow-md px-1 py-1"
        >
          <Btn
            on={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Bold"
            testid="rt-bold"
          >
            <BoldIcon className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="Italic"
            testid="rt-italic"
          >
            <ItalicIcon className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            title="Underline"
            testid="rt-underline"
          >
            <UnderlineIcon className="h-3.5 w-3.5" />
          </Btn>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <Btn
            on={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            active={editor.isActive("heading", { level: 1 })}
            title="Heading 1"
            testid="rt-h1"
          >
            <Heading1 className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            active={editor.isActive("heading", { level: 2 })}
            title="Heading 2"
            testid="rt-h2"
          >
            <Heading2 className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            active={editor.isActive("heading", { level: 3 })}
            title="Heading 3"
            testid="rt-h3"
          >
            <Heading3 className="h-3.5 w-3.5" />
          </Btn>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <Btn
            on={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Bulleted list"
            testid="rt-ul"
          >
            <ListIcon className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Numbered list"
            testid="rt-ol"
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </Btn>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <Btn
            on={() => editor.chain().focus().setTextAlign("left").run()}
            active={editor.isActive({ textAlign: "left" })}
            title="Align left"
            testid="rt-align-left"
          >
            <AlignLeft className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() => editor.chain().focus().setTextAlign("center").run()}
            active={editor.isActive({ textAlign: "center" })}
            title="Align center"
            testid="rt-align-center"
          >
            <AlignCenter className="h-3.5 w-3.5" />
          </Btn>
          <Btn
            on={() => editor.chain().focus().setTextAlign("right").run()}
            active={editor.isActive({ textAlign: "right" })}
            title="Align right"
            testid="rt-align-right"
          >
            <AlignRight className="h-3.5 w-3.5" />
          </Btn>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <Btn
            on={() => {
              const url = window.prompt("URL");
              if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
              else editor.chain().focus().unsetLink().run();
            }}
            active={editor.isActive("link")}
            title="Insert link"
            testid="rt-link"
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </Btn>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <ColorPicker
            icon={<PaletteIcon className="h-3.5 w-3.5" />}
            title="Text color"
            testid="rt-color"
            colors={FONT_COLORS}
            onPick={(c) => editor.chain().focus().setColor(c).run()}
            onClear={() => editor.chain().focus().unsetColor().run()}
          />
          <ColorPicker
            icon={<HighlighterIcon className="h-3.5 w-3.5" />}
            title="Highlight"
            testid="rt-highlight"
            colors={HIGHLIGHT_COLORS}
            onPick={(c) =>
              editor.chain().focus().setHighlight({ color: c }).run()
            }
            onClear={() => editor.chain().focus().unsetHighlight().run()}
          />
          {enableSnippets && (
            <>
              <span className="w-px h-4 bg-slate-200 mx-1" />
              <Btn
                on={async () => {
                  if (!snippetsLoaded) {
                    try {
                      const { data } = await api.get("/snippets");
                      setSnippets(data || []);
                    } catch {
                      /* ignore — show empty */
                    }
                    setSnippetsLoaded(true);
                  }
                  setSnippetsOpen((o) => !o);
                }}
                active={snippetsOpen}
                title="Insert from snippet library"
                testid="rt-snippet"
              >
                <LibraryIcon className="h-3.5 w-3.5" />
              </Btn>
            </>
          )}
        </BubbleMenu>
      )}
      {snippetsOpen && enableSnippets && (
        <div
          className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-2 w-72 max-h-80 overflow-y-auto"
          style={{ top: "100%", left: 0 }}
          data-testid="rt-snippet-picker"
        >
          <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Snippet library
          </div>
          {snippets.length === 0 ? (
            <div className="text-xs text-slate-500 px-2 py-3">
              No snippets yet. Create some under Settings → Snippets.
            </div>
          ) : (
            snippets.map((s) => (
              <button
                key={s.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  editor.chain().focus().insertContent(s.content || "").run();
                  setSnippetsOpen(false);
                  // best-effort usage bump
                  api.post(`/snippets/${s.id}/used`).catch(() => {});
                }}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-emerald-50"
                data-testid={`rt-snippet-${s.id}`}
              >
                <div className="text-sm font-medium text-slate-900 truncate">
                  {s.name}
                </div>
                {s.category && (
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">
                    {s.category}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
